import { useSyncExternalStore } from 'react';

/**
 * Which goals the chat Status column shows beside the full-screen chat, so the chat's goal card skips a plan the
 * column already draws.
 */
interface ShownColumn {
  conversationId: string;
  goalIds: ReadonlySet<string>;
}

let shown: ShownColumn | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of [...listeners]) listener();
}

function sameIds(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  return a.size === b.size && [...a].every((id) => b.has(id));
}

export function showStatusColumn(conversationId: string, goalIds: Iterable<string>): void {
  const next = new Set(goalIds);
  if (shown && shown.conversationId === conversationId && sameIds(shown.goalIds, next)) return;
  shown = { conversationId, goalIds: next };
  notify();
}

/** Only the conversation that showed the column can hide it, so a late unmount cannot hide the next one's. */
export function hideStatusColumn(conversationId: string): void {
  if (shown?.conversationId !== conversationId) return;
  shown = null;
  notify();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function statusColumnShowsGoal(conversationId: string | undefined, goalId: string | undefined): boolean {
  return !!conversationId && !!goalId && shown?.conversationId === conversationId && shown.goalIds.has(goalId);
}

export function useStatusColumnShowsGoal(conversationId: string | undefined, goalId: string | undefined): boolean {
  return useSyncExternalStore(
    subscribe,
    () => statusColumnShowsGoal(conversationId, goalId),
    () => false,
  );
}
